import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../stores/hooks/hooks.ts";
import { useEffect, useState } from "react";
import { inviteUserThunk } from "../../../../../stores/thunks/companyThunk.tsx";
import {
  RevButton,
  RevDialog,
  RevRadioGroup,
  RevTextAreaWithChips,
} from "@revmatics/catalyst";
import { roleOptions } from "./userData.ts";

export const InviteUserDialog = ({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) => {
  const dispatch = useAppDispatch();
  const { isInviting } = useAppSelector((state) => state.company);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [emails, setEmails] = useState<string[]>([]);
  const [error, setError] = useState<string>("");

  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (isOpen) {
      setSelectedRole(roleOptions[0]?.value || "");
    } else {
      setEmails([]);
      setInputValue("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!inputValue && error) {
      setError("Required field.");
    }
  }, [error, inputValue]);

  const handleSendInvites = () => {
    const trimmedInput = inputValue.trim();
    let finalEmails = [...emails];

    if (trimmedInput && /^\S+@\S+\.\S+$/.test(trimmedInput)) {
      finalEmails = [...new Set([...emails, trimmedInput])];
    }

    if (finalEmails.length === 0) {
      setError("Email isn't valid.");
      return;
    }

    dispatch(inviteUserThunk({ emails: finalEmails, role: selectedRole }))
      .unwrap()
      .then(() => {
        setError("");
        setIsOpen(false);
      });
  };

  const handleRoleChange = (value: string | number) => {
    setSelectedRole(String(value));
  };

  const dialogActions = [
    <RevButton
      key="cancel"
      text="Cancel"
      variant="secondary"
      handleClick={() => {
        setError("");
        setIsOpen(false);
      }}
    />,
    <RevButton
      key="send"
      text="Send Invites"
      variant="primary"
      handleClick={handleSendInvites}
      disabled={emails.length === 0 && !inputValue.trim()}
      isLoading={isInviting}
    />,
  ];

  return (
    <RevDialog
      isOpen={isOpen}
      setIsOpen={(value) => {
        setError("");
        setIsOpen(value);
      }}
      title="Invite users"
      btnList={dialogActions}
      mobileSize="xs"
    >
      <div className="space-y-4 w-full">
        <div>
          <h6 className="mb-2">Select Role</h6>
          <RevRadioGroup
            name="role"
            options={roleOptions}
            value={selectedRole}
            onChange={handleRoleChange}
            displayDirection="vertical"
          />
        </div>
        <RevTextAreaWithChips
          label="Emails"
          isLabelBold
          placeholder="Enter email addresses"
          helperText="Email addresses, separated by comma, space, or return"
          emails={emails}
          onEmailsChange={setEmails}
          value={inputValue}
          onChange={setInputValue}
          error={error}
          setError={setError}
        />
      </div>
    </RevDialog>
  );
};
